import React, { useState, useEffect, useCallback } from 'react';
import MonitoringDashboard from './MonitoringDashboard';
import './AdminDashboard.css';

const ADMIN_API = 'https://5xijfjcbwfjwnxppy4qauwdtvu0kdiil.lambda-url.us-east-1.on.aws';
const SELLER_API = 'https://dmk7gtgi23d5kg4xkwoln7cedu0telqy.lambda-url.us-east-1.on.aws';

const AdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [chatMetrics, setChatMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [picks, setPicks] = useState([]);
  const [sellerApps, setSellerApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('idToken')}`
  }), []);

  const fetchData = useCallback(async (endpoint, setter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${ADMIN_API}${endpoint}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch');
      setter(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (activeTab === 'stats') fetchData('/admin/stats', setStats);
    else if (activeTab === 'analytics') fetchData('/admin/analytics', setAnalytics);
    else if (activeTab === 'chat') fetchData('/admin/chat-metrics', setChatMetrics);
    else if (activeTab === 'users') fetchData('/admin/users', d => setUsers(d.users || []));
    else if (activeTab === 'feedback') fetchData('/admin/feedback?status=all', d => setFeedback(d.feedback || []));
    else if (activeTab === 'picks') fetchData('/admin/picks', d => setPicks(d.picks || []));
    else if (activeTab === 'sellers') fetchSellerApps();
  }, [activeTab, fetchData]);

  const fetchSellerApps = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SELLER_API}/api/admin/seller-applications`, { headers: getAuthHeaders() });
      const data = await res.json();
      setSellerApps(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const handleSellerAction = async (appId, action) => {
    try {
      await fetch(`${SELLER_API}/api/admin/seller-applications/${appId}/${action}`, {
        method: 'POST', headers: getAuthHeaders()
      });
      fetchSellerApps();
    } catch (e) { setError(e.message); }
  };

  const updateUser = async (userId, updates) => {
    try {
      await fetch(`${ADMIN_API}/admin/users`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, ...updates })
      });
      fetchData('/admin/users', d => setUsers(d.users || []));
    } catch (e) { setError(e.message); }
  };

  const updateFeedback = async (feedbackId, status) => {
    try {
      await fetch(`${ADMIN_API}/admin/feedback`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ feedback_id: feedbackId, status })
      });
      fetchData('/admin/feedback?status=all', d => setFeedback(d.feedback || []));
    } catch (e) { setError(e.message); }
  };

  const updatePick = async (pickId, updates) => {
    try {
      await fetch(`${ADMIN_API}/admin/picks`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ pick_id: pickId, ...updates })
      });
      fetchData('/admin/picks', d => setPicks(d.picks || []));
    } catch (e) { setError(e.message); }
  };

  const searchUsers = () => fetchData(`/admin/users?search=${encodeURIComponent(searchTerm)}`, d => setUsers(d.users || []));

  if (error === 'Admin access required') {
    return <div className="admin-dashboard"><div className="admin-error">⛔ Admin access required</div></div>;
  }

  return (
    <div className="admin-dashboard">
      <h1>🛠️ Admin Dashboard</h1>
      
      <div className="admin-tabs">
        {['analytics', 'chat', 'monitoring', 'stats', 'users', 'feedback', 'picks', 'sellers'].map(tab => (
          <button key={tab} className={activeTab === tab ? 'active' : ''} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'sellers' && sellerApps.length > 0 && <span className="badge">{sellerApps.length}</span>}
          </button>
        ))}
      </div>

      {error && <div className="admin-error">{error}</div>}
      {loading && <div className="admin-loading">Loading...</div>}

      {activeTab === 'analytics' && analytics && (
        <div className="analytics-section">
          <div className="stats-grid">
            <div className="stat-card"><h3>{analytics.signups_today}</h3><p>Signups Today</p></div>
            <div className="stat-card"><h3>{analytics.signups_week}</h3><p>Signups This Week</p></div>
            <div className="stat-card"><h3>{analytics.bets_today}</h3><p>Bets Today</p></div>
            <div className="stat-card"><h3>{analytics.bets_week}</h3><p>Bets This Week</p></div>
            <div className="stat-card"><h3>{analytics.active_users_week}</h3><p>Active Users (7d)</p></div>
            <div className="stat-card"><h3>{analytics.feedback_by_status?.new || 0}</h3><p>New Feedback</p></div>
          </div>
          
          <div className="analytics-panels">
            <div className="panel">
              <h3>📊 Activity Today</h3>
              <ul>
                {Object.entries(analytics.activity_today || {}).map(([type, count]) => (
                  <li key={type}><strong>{type.replace('_', ' ')}</strong>: {count}</li>
                ))}
                {Object.keys(analytics.activity_today || {}).length === 0 && <li className="muted">No activity yet</li>}
              </ul>
            </div>
            <div className="panel">
              <h3>📄 Page Views Today</h3>
              <ul>
                {Object.entries(analytics.page_views_today || {}).map(([page, count]) => (
                  <li key={page}><strong>{page || 'home'}</strong>: {count}</li>
                ))}
                {Object.keys(analytics.page_views_today || {}).length === 0 && <li className="muted">No page views yet</li>}
              </ul>
            </div>
            <div className="panel">
              <h3>🆕 Recent Signups</h3>
              <ul>
                {analytics.recent_signups?.map((u, i) => (
                  <li key={i}><strong>{u.name}</strong> - {u.email} <span className="muted">({u.date?.slice(0, 10)})</span></li>
                ))}
              </ul>
            </div>
            <div className="panel">
              <h3>⚡ Recent Activity</h3>
              <ul>
                {analytics.recent_activity?.map((a, i) => (
                  <li key={i}><strong>{a.type}</strong> {a.page && `(${a.page})`} - {a.email || 'anon'} <span className="muted">{a.time?.slice(11, 16)}</span></li>
                ))}
                {(!analytics.recent_activity || analytics.recent_activity.length === 0) && <li className="muted">No activity yet</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'chat' && chatMetrics && (
        <div className="analytics-section">
          <div className="stats-grid">
            <div className="stat-card"><h3>{chatMetrics.invocations_today}</h3><p>Chat Messages Today</p></div>
            <div className="stat-card"><h3>{chatMetrics.total_invocations_week}</h3><p>Messages This Week</p></div>
            <div className="stat-card"><h3>{(chatMetrics.avg_duration_ms / 1000).toFixed(1)}s</h3><p>Avg Response Time</p></div>
            <div className="stat-card"><h3>{chatMetrics.errors_week}</h3><p>Errors This Week</p></div>
          </div>
          
          <div className="analytics-panels">
            <div className="panel wide">
              <h3>💬 Daily Chat Volume (Last 7 Days)</h3>
              <div className="chart-bars">
                {Object.entries(chatMetrics.daily_invocations || {}).map(([date, count]) => (
                  <div key={date} className="bar-item">
                    <div className="bar" style={{ height: `${Math.min(count * 2, 100)}px` }}></div>
                    <span className="bar-label">{date.slice(5)}</span>
                    <span className="bar-value">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'monitoring' && <MonitoringDashboard />}

      {activeTab === 'stats' && stats && (
        <div className="stats-grid">
          <div className="stat-card"><h3>{stats.total_users}</h3><p>Total Users</p></div>
          <div className="stat-card"><h3>{stats.active_subscribers}</h3><p>Active Subscribers</p></div>
          <div className="stat-card"><h3>{stats.new_feedback}</h3><p>New Feedback</p></div>
          <div className="stat-card"><h3>{stats.total_picks}</h3><p>Total Picks</p></div>
          <div className="stat-card won"><h3>{stats.picks_won}</h3><p>Picks Won</p></div>
          <div className="stat-card lost"><h3>{stats.picks_lost}</h3><p>Picks Lost</p></div>
          <div className="stat-card"><h3>{stats.picks_pending}</h3><p>Pending</p></div>
          <div className="stat-card">
            <h3>{stats.picks_won + stats.picks_lost > 0 ? ((stats.picks_won / (stats.picks_won + stats.picks_lost)) * 100).toFixed(1) : 0}%</h3>
            <p>Win Rate</p>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="admin-section">
          <div className="search-bar">
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by email or name..." onKeyDown={e => e.key === 'Enter' && searchUsers()} />
            <button onClick={searchUsers}>Search</button>
          </div>
          <table className="admin-table">
            <thead><tr><th>Email</th><th>Name</th><th>Status</th><th>Admin</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id}>
                  <td>{u.email}</td><td>{u.display_name}</td>
                  <td><select value={u.subscription_status} onChange={e => updateUser(u.user_id, { subscription_status: e.target.value })}>
                    <option value="trial">Trial</option><option value="active">Active</option><option value="cancelled">Cancelled</option><option value="expired">Expired</option>
                  </select></td>
                  <td><input type="checkbox" checked={u.is_admin} onChange={e => updateUser(u.user_id, { is_admin: e.target.checked })} /></td>
                  <td>{u.created_at?.slice(0, 10)}</td>
                  <td><button className="small" onClick={() => navigator.clipboard.writeText(u.user_id)}>Copy ID</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'feedback' && (
        <div className="admin-section">
          <table className="admin-table">
            <thead><tr><th>Date</th><th>Email</th><th>Type</th><th>Message</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {feedback.map(f => (
                <tr key={f.feedback_id} className={f.status === 'new' ? 'highlight' : ''}>
                  <td>{f.created_at?.slice(0, 10)}</td><td>{f.email || 'N/A'}</td><td>{f.category}</td>
                  <td className="message-cell">{f.message?.slice(0, 100)}{f.message?.length > 100 ? '...' : ''}</td>
                  <td><span className={`status-badge ${f.status}`}>{f.status}</span></td>
                  <td>
                    {f.status === 'new' && <button className="small" onClick={() => updateFeedback(f.feedback_id, 'reviewed')}>Mark Reviewed</button>}
                    {f.status === 'reviewed' && <button className="small" onClick={() => updateFeedback(f.feedback_id, 'resolved')}>Resolve</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'picks' && (
        <div className="admin-section">
          <table className="admin-table">
            <thead><tr><th>Card</th><th>Matchup</th><th>Selection</th><th>Odds</th><th>Confidence</th><th>Result</th><th>Game Time</th><th>Actions</th></tr></thead>
            <tbody>
              {picks.map(p => (
                <tr key={p.pick_id} className={p.result !== 'pending' ? p.result : ''}>
                  <td>{p.card_key}</td><td>{p.matchup}</td><td>{p.selection}</td><td>{p.odds > 0 ? '+' : ''}{p.odds}</td><td>{p.confidence}</td>
                  <td><select value={p.result} onChange={e => updatePick(p.pick_id, { result: e.target.value })}>
                    <option value="pending">Pending</option><option value="won">Won</option><option value="lost">Lost</option><option value="push">Push</option>
                  </select></td>
                  <td>{p.game_time?.slice(0, 16).replace('T', ' ')}</td>
                  <td><button className="small danger" onClick={() => { if(window.confirm('Delete?')) updatePick(p.pick_id, {}) }}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'sellers' && (
        <div className="admin-section">
          <h3>📝 Pending Seller Applications</h3>
          {sellerApps.length === 0 ? (
            <p className="muted">No pending applications</p>
          ) : (
            <table className="admin-table">
              <thead><tr><th>Date</th><th>User ID</th><th>Display Name</th><th>Bio</th><th>Actions</th></tr></thead>
              <tbody>
                {sellerApps.map(app => (
                  <tr key={app.application_id}>
                    <td>{app.created_at?.slice(0, 10)}</td>
                    <td className="user-id">{app.user_id?.slice(0, 20)}...</td>
                    <td><strong>{app.display_name}</strong></td>
                    <td className="message-cell">{app.bio?.slice(0, 100)}{app.bio?.length > 100 ? '...' : ''}</td>
                    <td>
                      <button className="small success" onClick={() => handleSellerAction(app.application_id, 'approve')}>✓ Approve</button>
                      <button className="small danger" onClick={() => handleSellerAction(app.application_id, 'reject')}>✗ Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
